<template>
  <div id="app" class="app-layout">
    <div class="app-main">
      <header class="app-header">
        <div class="title-row">
          <h1 class="game-title">
            <span class="title-brand">
              <span class="title-cn">{{ t('title.cn') }}</span>
              <span class="title-sep" aria-hidden="true">·</span>
              <span class="title-en">{{ t('title.en') }}</span>
            </span>
            <button
              type="button"
              class="help-q"
              :aria-label="t('help.aboutAria')"
              title="?"
              @click="aboutOpen = true"
            >?</button>
            <span class="title-version">{{ t('meta.versionLabel') }}</span>
          </h1>
          <select
            id="locale-select"
            class="lang-select title-lang-select"
            :value="locale"
            @change="onLocaleChange($event)"
          >
            <option
              v-for="opt in localeOptions"
              :key="opt.code"
              :value="opt.code"
            >
              {{ opt.native }}
            </option>
          </select>
        </div>
      </header>

      <Teleport to="body">
        <div
          v-if="aboutOpen"
          class="about-overlay"
          role="presentation"
          @click.self="aboutOpen = false"
        >
          <div
            class="about-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="about-h"
            @keydown.escape="aboutOpen = false"
          >
            <h2 id="about-h" class="about-h">{{ t('about.title') }}</h2>
            <p class="about-body">{{ t('about.body') }}</p>
            <button type="button" class="btn about-close" @click="aboutOpen = false">
              {{ t('dialog.close') }}
            </button>
          </div>
        </div>
      </Teleport>

      <div class="controls-row">
        <ModeSwitch
          class="controls-row__modes"
          :current-mode="gameMode"
          @mode-change="switchMode"
        />
        <label class="assist-toggle">
          <input
            v-model="showMoveAssist"
            type="checkbox"
            class="assist-toggle__input"
          >
          <span>{{ t('mode.moveAssist') }}</span>
        </label>
      </div>
      <div v-if="gameMode === 'pvn'" class="network-panel board-width-line">
        <div class="network-join-row">
          <input
            v-model="networkPlayerName"
            class="network-name-input"
            type="text"
            maxlength="24"
            :placeholder="t('network.namePlaceholder')"
          >
          <input
            v-model="roomCodeInput"
            class="network-room-input"
            type="text"
            inputmode="numeric"
            maxlength="6"
            :placeholder="t('network.roomPlaceholder')"
          >
          <button type="button" class="network-join-btn" @click="onJoinRoom">
            {{ t('network.join') }}
          </button>
          <button type="button" class="network-leave-btn" @click="onLeaveRoom">
            {{ t('network.leave') }}
          </button>
        </div>
        <div class="network-status-line">{{ networkStatusText }}</div>
        <div v-if="opponentDisplayName" class="network-opponent-line">
          {{ t('network.opponent') }}{{ opponentDisplayName }}
        </div>
      </div>

      <!-- 计分板（两侧为玩家名称输入） -->
      <div class="board-width-line">
        <Scoreboard
          :scores="scores"
          :game-mode="gameMode"
          :current-player="currentPlayer"
          :game-over="gameOver"
          :winner="winner"
          :black-name="playerNameBlack"
          :white-name="playerNameWhite"
          @update:black-name="playerNameBlack = $event"
          @update:white-name="playerNameWhite = $event"
        />
      </div>

      <!-- 状态信息（固定占位，避免复盘后布局跳动） -->
      <div class="status-block board-width-line">
        <div class="status-text">
          <template v-if="showTurnStatus">
            <span class="highlight">{{ statusBannerRich.beforeName }}</span>
            <span class="status-emph">{{ statusBannerRich.name }}</span>
            <span class="highlight">{{ statusBannerRich.between }}</span>
            <span class="status-emph">{{ statusBannerRich.sec }}</span>
            <span class="highlight">{{ statusBannerRich.afterSec }}</span>
          </template>
        </div>
        <div class="skip-message">
          <span v-if="showTurnStatus && skipMessage">{{ skipMessage }}</span>
        </div>
        <div
          v-if="gameOver"
          class="status-replay-nav"
          role="group"
          :aria-label="t('side.replayNav')"
        >
          <button
            type="button"
            class="status-replay-btn status-replay-btn--play-again"
            :disabled="gameMode === 'pvn' && rematchWaiting"
            @click="onPlayAgain"
          >
            {{ t('side.playAgain') }}
          </button>
          <button
            v-if="moveLog.length"
            type="button"
            class="status-replay-btn"
            :disabled="replayCursor <= 0"
            :aria-label="t('side.prevStep')"
            :title="t('side.prevStep')"
            @click="onReplayPrev"
          >
            ←
          </button>
          <button
            v-if="moveLog.length"
            type="button"
            class="status-replay-btn"
            :disabled="replayCursor >= moveLog.length"
            :aria-label="t('side.nextStep')"
            :title="t('side.nextStep')"
            @click="onReplayNext"
          >
            →
          </button>
        </div>
      </div>

      <!-- 棋盘 + 右侧对局进程（等高，进程区可滚动） -->
      <div class="board-row">
        <div ref="boardWrapRef" class="board-wrap">
          <Board
            :board="displayBoard"
            :piece-metas="displayPieceMetas"
            :valid-move-set="validMoveSet"
            :move-gain-map="moveAssistMap"
            :show-move-gain="showMoveAssist"
            :last-move-index="displayLastMoveIndex"
            :can-interact="boardCanInteract"
            :game-over="gameOver"
            @cell-click="handleCellClick"
          />
        </div>
        <div
          class="side-track"
          :class="{
            'side-track--mobile': isNarrowLayout,
            'side-track--network': gameMode === 'pvn'
          }"
          :style="sideTrackStyle"
        >
          <GameSidePanel
            :move-log="moveLog"
            :game-over="gameOver"
            :replay-cursor="replayCursor"
            :scores="scores"
            :result-title="resultTitle"
            :black-display-name="displayNameBlack"
            :white-display-name="displayNameWhite"
            :local-saves="localSaves"
            @replay-step="setReplayStep"
            @export-game="downloadGameRecord"
            @import-game="onPickImportFile"
            @save-local="onSaveLocal"
            @load-local-save="onLoadLocalSave"
            @remove-local-save="onRemoveLocalSave"
          />
        </div>
      </div>
      <input
        ref="importFileInput"
        type="file"
        class="sr-only"
        accept="application/json,.json"
        @change="onImportGameFile"
      />
    </div>
    <footer class="app-footer">
      <small>Copyright &copy; {{ new Date().getFullYear() }} Kyle Tang. All rights reserved.</small>
    </footer>
  </div>
</template>

<script setup>
import {
  watch,
  onMounted,
  onUnmounted,
  ref,
  computed,
  nextTick
} from 'vue'
import { useGameState } from './composables'
import { Board, Scoreboard, ModeSwitch, GameSidePanel } from './components'
import { locale, localeOptions, setLocale, t } from '@/i18n/index.js'
import { createRealtimeClient } from '@/network/realtimeClient.js'

const NETWORK_NAME_STORAGE_KEY = 'othello-network-player-name'
const PUBLIC_WS_URL = 'wss://nas.1yu.cn:12345'

const boardWrapRef = ref(null)
const importFileInput = ref(null)
const aboutOpen = ref(false)
const showMoveAssist = ref(false)
const sideTrackPx = ref(null)
const isNarrowLayout = ref(false)
const roomCodeInput = ref('')
const networkPlayerName = ref('')
const netState = ref('idle')
const localColor = ref('')
const matchedPlayers = ref({ blackName: '', whiteName: '' })
const countdownSec = ref(0)
const networkErrorMsg = ref('')
const partnerAwayMsg = ref('')
const rematchWaiting = ref(false)
const manualDisconnecting = ref(false)
const connectedServerLabel = ref('')
let countdownTimerId = null
let realtimeClient = null
let connectSequenceId = 0

const mqNarrow = '(max-width: 900px)'

function checkNarrow() {
  isNarrowLayout.value =
    typeof window !== 'undefined' && window.matchMedia(mqNarrow).matches
}

function measureSideTrack() {
  checkNarrow()
  if (isNarrowLayout.value) {
    sideTrackPx.value = null
    return
  }
  const el = boardWrapRef.value
  if (!el) return
  const h = Math.round(el.getBoundingClientRect().height)
  if (h > 0) {
    sideTrackPx.value = h
  }
}

const sideTrackStyle = computed(() => {
  if (isNarrowLayout.value || sideTrackPx.value == null) {
    return {}
  }
  const networkExtra = gameMode.value === 'pvn' ? 98 : 0
  return { height: `${sideTrackPx.value + 176 + networkExtra}px` }
})

const networkStatusText = computed(() => {
  void locale.value
  if (networkErrorMsg.value) {
    return `${t('network.error')}${networkErrorMsg.value}`
  }
  const connectedPrefix = connectedServerLabel.value
    ? `已连接到${connectedServerLabel.value}。`
    : ''
  if (partnerAwayMsg.value) {
    return `${connectedPrefix}${partnerAwayMsg.value}`
  }
  if (netState.value === 'connecting') return t('network.connecting')
  if (netState.value === 'joined') return `${connectedPrefix}${t('network.ready')}`
  if (netState.value === 'matched') {
    return `${connectedPrefix}${t('network.matchedCountdown', { sec: countdownSec.value })}`
  }
  if (netState.value === 'playing') return `${connectedPrefix}${t('network.playing')}`
  if (netState.value === 'waiting-rematch') return `${connectedPrefix}${t('network.waitingRematch')}`
  if (netState.value === 'closed') return t('network.closed')
  return t('network.idle')
})

const opponentDisplayName = computed(() => {
  if (!localColor.value) return ''
  if (localColor.value === 'black') return matchedPlayers.value.whiteName
  return matchedPlayers.value.blackName
})

let resizeObserver = null
let mqListener = null

onMounted(() => {
  checkNarrow()
  mqListener = () => measureSideTrack()
  window.matchMedia(mqNarrow).addEventListener('change', mqListener)
  window.addEventListener('resize', measureSideTrack)

  nextTick(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => measureSideTrack())
    })
    const el = boardWrapRef.value
    if (el && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => measureSideTrack())
      resizeObserver.observe(el)
    }
  })
  if (typeof localStorage !== 'undefined') {
    try {
      const saved = localStorage.getItem(NETWORK_NAME_STORAGE_KEY)
      if (saved && saved.trim()) {
        networkPlayerName.value = saved.trim().slice(0, 24)
        return
      }
    } catch {
      // ignore
    }
  }
  networkPlayerName.value = String(playerNameBlack.value || '').trim()
})

onUnmounted(() => {
  window.removeEventListener('resize', measureSideTrack)
  if (typeof window !== 'undefined' && mqListener) {
    window.matchMedia(mqNarrow).removeEventListener('change', mqListener)
  }
  resizeObserver?.disconnect()
  disconnectNetwork()
})

// 使用游戏状态管理
const {
  currentPlayer,
  gameMode,
  playerNameBlack,
  playerNameWhite,
  displayNameBlack,
  displayNameWhite,
  gameOver,
  winner,
  skipMessage,
  aiThinking,
  scores,
  validMoveSet,
  moveAssistMap,
  canInteract,
  statusBannerRich,
  resultTitle,
  moveLog,
  replayCursor,
  localSaves,
  buildExportJson,
  displayBoard,
  displayPieceMetas,
  displayLastMoveIndex,
  initGame,
  handlePlayerMove,
  makeAIMove,
  switchMode,
  setReplayStep,
  downloadGameRecord,
  importGameFromJson,
  saveCurrentToLocalStorage,
  loadSavedFromLocal,
  removeSavedFromLocal
} = useGameState()

const boardCanInteract = computed(() => {
  if (gameMode.value !== 'pvn') {
    return canInteract.value
  }
  if (netState.value !== 'playing') return false
  if (localColor.value === 'black' && currentPlayer.value !== 1) return false
  if (localColor.value === 'white' && currentPlayer.value !== 2) return false
  return canInteract.value
})

const showTurnStatus = computed(() => {
  if (gameOver.value) return false
  if (gameMode.value !== 'pvn') return true
  return netState.value === 'playing'
})

function onLocaleChange(ev) {
  setLocale(ev.target.value)
}

watch(
  locale,
  () => {
    if (typeof document !== 'undefined') {
      document.title = t('meta.pageTitle')
    }
  },
  { immediate: true }
)

// 监听当前玩家变化，触发AI下棋
watch(currentPlayer, (newPlayer) => {
  if (gameMode.value === 'pvc' && newPlayer === 2 && !gameOver.value) {
    aiThinking.value = true
    // 延迟模拟AI思考时间
    setTimeout(() => {
      makeAIMove()
    }, 450)
  }
})

watch(gameMode, (mode) => {
  if (mode !== 'pvn') {
    disconnectNetwork()
  }
})

watch(
  gameOver,
  (over, prev) => {
    if (!over || prev || gameMode.value !== 'pvn') return
    if (!realtimeClient || !localColor.value) return
    const blackScore = Number(scores.value?.black || 0)
    const whiteScore = Number(scores.value?.white || 0)
    realtimeClient.send({
      type: 'match-finished',
      blackScore,
      whiteScore,
      totalPieces: blackScore + whiteScore,
      finalJson: buildExportJson()
    })
  },
  { flush: 'post' }
)

watch(networkPlayerName, (name) => {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(
      NETWORK_NAME_STORAGE_KEY,
      String(name ?? '').trim().slice(0, 24)
    )
  } catch {
    // ignore
  }
})

// 处理棋盘点击
function handleCellClick(index) {
  if (gameMode.value !== 'pvn') {
    handlePlayerMove(index)
    return
  }
  if (!boardCanInteract.value) return
  const ok = handlePlayerMove(index)
  if (ok) {
    realtimeClient?.send({ type: 'move', index })
  }
}

function normalizeRoomCode(v) {
  return String(v ?? '').replace(/\D/g, '').slice(0, 6)
}

function getLocalWsUrl() {
  return 'ws://localhost:8080'
}

function shouldUseLocalWs() {
  if (typeof window === 'undefined') return true
  return window.location.hostname === 'localhost'
}

function resetCountdownTimer() {
  if (countdownTimerId != null) {
    clearInterval(countdownTimerId)
    countdownTimerId = null
  }
}

function applyMatchedPlayers(players) {
  const blackName = String(players?.blackName || '').trim()
  const whiteName = String(players?.whiteName || '').trim()
  matchedPlayers.value = { blackName, whiteName }
  if (blackName) {
    playerNameBlack.value = blackName
  }
  if (whiteName) {
    playerNameWhite.value = whiteName
  }
}

function startMatchCountdown(sec = 3) {
  resetCountdownTimer()
  rematchWaiting.value = false
  countdownSec.value = sec
  netState.value = 'matched'
  countdownTimerId = window.setInterval(() => {
    countdownSec.value -= 1
    if (countdownSec.value <= 0) {
      resetCountdownTimer()
      initGame()
      netState.value = 'playing'
    }
  }, 1000)
}

function handleRealtimeMessage(data) {
  if (!data || typeof data !== 'object') return
  if (data.type === 'joined') {
    localColor.value = data.color || ''
    applyMatchedPlayers(data.players)
    netState.value = 'joined'
    networkErrorMsg.value = ''
    partnerAwayMsg.value = ''
    return
  }
  if (data.type === 'partner-away') {
    partnerAwayMsg.value =
      data.reason === 'leave'
        ? t('network.partnerLeft')
        : t('network.partnerDisconnected')
    return
  }
  if (data.type === 'room-update') {
    networkErrorMsg.value = ''
    applyMatchedPlayers(data.players)
    if (data.matched) {
      partnerAwayMsg.value = ''
      startMatchCountdown(3)
    } else {
      netState.value = 'joined'
    }
    return
  }
  if (data.type === 'move') {
    handlePlayerMove(Number(data.index))
    return
  }
  if (data.type === 'rematch-waiting') {
    rematchWaiting.value = true
    netState.value = 'waiting-rematch'
    return
  }
  if (data.type === 'rematch-invite') {
    const ok = window.confirm(t('network.rematchInviteConfirm'))
    realtimeClient?.send({ type: 'rematch-response', accept: Boolean(ok) })
    if (!ok) {
      onLeaveRoom()
    }
    return
  }
  if (data.type === 'rematch-start') {
    rematchWaiting.value = false
    initGame()
    netState.value = 'playing'
    return
  }
  if (data.type === 'error') {
    if (data.code === 'ROOM_FULL') {
      window.alert(t('network.roomFull'))
      onLeaveRoom()
      return
    }
    if (data.code === 'NAME_TAKEN') {
      window.alert(t('network.nameTaken'))
      onLeaveRoom()
      return
    }
    networkErrorMsg.value = data.message || 'unknown error'
  }
}

function disconnectNetwork() {
  connectSequenceId += 1
  resetCountdownTimer()
  rematchWaiting.value = false
  if (realtimeClient) {
    manualDisconnecting.value = true
    realtimeClient.send({ type: 'leave' })
    realtimeClient.close()
    realtimeClient = null
  }
  localColor.value = ''
  matchedPlayers.value = { blackName: '', whiteName: '' }
  connectedServerLabel.value = ''
  netState.value = 'idle'
  networkErrorMsg.value = ''
  partnerAwayMsg.value = ''
}

function onLeaveRoom() {
  disconnectNetwork()
}

function onJoinRoom() {
  const roomCode = normalizeRoomCode(roomCodeInput.value)
  roomCodeInput.value = roomCode
  if (!/^\d{6}$/.test(roomCode)) {
    window.alert(t('network.invalidRoomCode'))
    return
  }
  const myName = String(networkPlayerName.value || '').trim().slice(0, 24) || t('player.default1')
  networkPlayerName.value = myName
  playerNameBlack.value = myName
  disconnectNetwork()
  netState.value = 'connecting'
  const attempts = shouldUseLocalWs()
    ? [{ url: getLocalWsUrl(), label: '本地服务器' }]
    : [{ url: PUBLIC_WS_URL, label: '公共服务器' }]

  const seqId = ++connectSequenceId
  const connectAt = (idx) => {
    if (seqId !== connectSequenceId) return
    const attempt = attempts[idx]
    realtimeClient = createRealtimeClient({
      serverUrl: attempt.url,
      roomCode,
      playerName: myName,
      onOpen: () => {
        if (seqId !== connectSequenceId) return
        networkErrorMsg.value = ''
        partnerAwayMsg.value = ''
      },
      onClose: () => {
        if (seqId !== connectSequenceId) return
        const manual = manualDisconnecting.value
        manualDisconnecting.value = false
        if (manual) {
          if (gameMode.value === 'pvn') netState.value = 'idle'
          return
        }
        if (gameMode.value === 'pvn') {
          netState.value = 'closed'
        }
      },
      onError: (err) => {
        if (seqId !== connectSequenceId) return
        networkErrorMsg.value = err?.message || String(err)
      },
      onMessage: (data) => {
        if (seqId !== connectSequenceId) return
        if (data?.type === 'joined') {
          connectedServerLabel.value = attempt.label
        }
        handleRealtimeMessage(data)
      }
    })
  }
  connectAt(0)
}

function onReplayPrev() {
  setReplayStep(replayCursor.value - 1)
}

function onPlayAgain() {
  if (gameMode.value !== 'pvn') {
    initGame()
    return
  }
  if (netState.value !== 'playing' && netState.value !== 'waiting-rematch') {
    initGame()
    return
  }
  rematchWaiting.value = true
  netState.value = 'waiting-rematch'
  realtimeClient?.send({ type: 'rematch-request' })
}

function onReplayNext() {
  setReplayStep(replayCursor.value + 1)
}

function onPickImportFile() {
  importFileInput.value?.click()
}

async function onImportGameFile(ev) {
  const input = ev.target
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  try {
    const text = await file.text()
    importGameFromJson(text)
    window.alert(t('side.importOk'))
    await nextTick()
    measureSideTrack()
  } catch (err) {
    window.alert(`${t('side.importFailed')}${err?.message || err}`)
  }
}

function onSaveLocal() {
  try {
    saveCurrentToLocalStorage()
    window.alert(t('side.saveOk'))
  } catch (err) {
    window.alert(
      err?.message === 'QUOTA' ? t('side.saveQuota') : err?.message || String(err)
    )
  }
}

async function onLoadLocalSave(id) {
  try {
    loadSavedFromLocal(id)
    window.alert(t('side.importOk'))
  } catch (err) {
    window.alert(`${t('side.importFailed')}${err?.message || err}`)
  }
}

function onRemoveLocalSave(id) {
  if (!window.confirm(t('side.confirmDeleteSave'))) return
  removeSavedFromLocal(id)
}

// 组件挂载时初始化游戏
onMounted(() => {
  initGame()
})
</script>

<style scoped>
.app-layout {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: min(960px, 100%);
}

.app-main {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 18px;
  width: 100%;
  min-width: 0;
  max-width: min(820px, 100%);
}

.app-footer {
  margin-top: 10px;
  padding: 8px 10px 16px;
  text-align: center;
  color: var(--text-gray, #94a3b8);
  font-size: 0.74rem;
  opacity: 0.9;
}

.app-header {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.title-row {
  width: 100%;
  max-width: min(820px, 100%);
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 10px;
}

.game-title {
  grid-column: 2;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px 10px;
  margin: 0;
  font-size: clamp(1.35rem, 4vw, 1.85rem);
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--accent-primary, #22c55e);
}

.title-brand {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.28em 0.4em;
}

.title-cn {
  display: inline-flex;
  align-items: center;
}

.title-sep {
  font-weight: 500;
  opacity: 0.55;
  user-select: none;
}

.title-en {
  font-weight: 700;
  letter-spacing: 0.06em;
}

.title-version {
  font-size: 0.72em;
  font-weight: 600;
  color: var(--text-gray, #94a3b8);
  margin-left: 4px;
}

.help-q {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.15em;
  height: 1.15em;
  padding: 0;
  margin: 0 1px;
  border: 1px solid rgba(148, 163, 184, 0.55);
  border-radius: 50%;
  font-size: 0.55em;
  line-height: 1;
  font-weight: 700;
  color: var(--text-gray, #94a3b8);
  background: rgba(15, 23, 42, 0.5);
  cursor: pointer;
  vertical-align: middle;
  transition:
    color 0.12s,
    border-color 0.12s,
    background 0.12s;
}

.help-q:hover {
  color: var(--accent-primary, #22c55e);
  border-color: rgba(34, 197, 94, 0.65);
  background: rgba(34, 197, 94, 0.12);
}

.controls-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px 14px;
  width: 100%;
  max-width: min(480px, 100%);
}

.controls-row__modes {
  flex-shrink: 0;
}

.network-panel {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: rgba(15, 23, 42, 0.45);
}

.network-join-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.network-room-input {
  flex: 0 0 92px;
  width: 92px;
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: #1f2937;
  color: #f8fafc;
  padding: 8px 10px;
  font: inherit;
  text-align: center;
  letter-spacing: 0.08em;
}

.network-name-input {
  flex: 1;
  min-width: 0;
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: #1f2937;
  color: #f8fafc;
  padding: 8px 10px;
  font: inherit;
}

.network-join-btn,
.network-leave-btn {
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  font: inherit;
  color: #fff;
  cursor: pointer;
}

.network-join-btn {
  background: #22c55e;
}

.network-leave-btn {
  background: #64748b;
}

.network-status-line,
.network-opponent-line {
  font-size: 0.85rem;
  color: #cbd5e1;
}

.assist-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.82rem;
  color: var(--text-gray, #94a3b8);
  user-select: none;
}

.assist-toggle__input {
  width: 14px;
  height: 14px;
  accent-color: var(--accent-primary, #22c55e);
}

.lang-select {
  font: inherit;
  font-size: 0.88rem;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: #1f2937;
  color: var(--text-primary, #f1f5f9);
  cursor: pointer;
}

.title-lang-select {
  grid-column: 3;
  justify-self: end;
  flex: 0 0 auto;
  min-width: 112px;
  margin-right: 26px;
  max-width: 140px;
}

.board-width-line {
  width: min(480px, 100%);
  max-width: min(480px, 100%);
}

.about-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(3px);
}

.about-dialog {
  max-width: 420px;
  width: 100%;
  padding: 20px 22px;
  border-radius: 12px;
  background: #1f2937;
  border: 1px solid rgba(148, 163, 184, 0.2);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.45);
}

.about-h {
  margin: 0 0 12px;
  font-size: 1.1rem;
  color: var(--accent-primary, #22c55e);
  text-align: center;
}

.about-body {
  margin: 0 0 18px;
  font-size: 0.9rem;
  line-height: 1.55;
  color: var(--text-primary, #e2e8f0);
}

.about-close {
  display: block;
  padding: 8px 20px;
  background: var(--accent-primary, #22c55e);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  margin: 0 auto;
}

.about-close:hover {
  opacity: 0.9;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.board-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 14px;
  width: 100%;
  min-width: 0;
}

.board-wrap {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  max-width: min(480px, 100%);
  aspect-ratio: 1;
}

.side-track {
  display: flex;
  flex-direction: column;
  flex: 0 0 clamp(220px, 31vw, 300px);
  width: clamp(220px, 31vw, 300px);
  max-width: clamp(220px, 31vw, 300px);
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  margin-top: -176px;
}

.side-track.side-track--network {
  margin-top: -274px;
}

.side-track.side-track--mobile {
  flex: none;
  width: min(480px, 100%);
  max-width: 100%;
  max-height: none;
  overflow: visible;
}

@media (max-width: 900px) {
  .title-row {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 8px;
  }

  .game-title {
    grid-column: 1;
    justify-content: flex-start;
    flex-wrap: nowrap;
    font-size: clamp(1.1rem, 5vw, 1.45rem);
    gap: 4px 6px;
    min-width: 0;
  }

  .title-brand {
    flex-wrap: nowrap;
    white-space: nowrap;
  }

  .title-lang-select {
    grid-column: 2;
    justify-self: end;
    margin-right: 0;
    min-width: 92px;
    max-width: 122px;
    padding: 5px 8px;
    font-size: 0.78rem;
  }

  .controls-row {
    justify-content: center;
    flex-wrap: nowrap;
    gap: 6px 8px;
    max-width: min(480px, 100%);
    overflow: hidden;
  }

  .assist-toggle {
    font-size: 0.76rem;
    gap: 4px;
    white-space: nowrap;
  }

  .assist-toggle__input {
    width: 12px;
    height: 12px;
  }

  .network-join-row {
    flex-wrap: wrap;
    gap: 6px;
  }

  .network-name-input {
    flex: 1 0 100%;
    width: 100%;
    min-width: 0;
  }

  .network-room-input {
    flex: 1 1 0;
    width: auto;
    min-width: 84px;
  }

  .board-row {
    flex-direction: column;
    align-items: center;
  }

  .board-wrap {
    flex: none;
    width: min(480px, 100%);
    max-width: 100%;
  }

  .side-track {
    margin-top: 0;
    height: auto;
    width: 100%;
    max-width: min(480px, 100%);
    flex: none;
  }

  .side-track.side-track--network {
    margin-top: 0;
  }
}

/* 状态文本高亮 */
.status-block {
  min-height: 44px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  text-align: center;
}

.status-replay-nav {
  display: flex;
  gap: 10px;
  margin-top: 6px;
}

.status-replay-btn {
  flex: 1;
  min-height: 42px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.5);
  color: var(--text-white, #f1f5f9);
  font-size: 1.25rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.12s, opacity 0.12s;
}

.status-replay-btn--play-again {
  font-size: 0.92rem;
  background: #22c55e;
  border-color: #22c55e;
  color: #ffffff;
}

.status-replay-btn--play-again:hover:not(:disabled) {
  background: #16a34a;
  border-color: #16a34a;
}

.status-replay-btn:hover:not(:disabled) {
  background: rgba(34, 197, 94, 0.18);
  border-color: rgba(34, 197, 94, 0.5);
}

.status-replay-btn:disabled {
  opacity: 0.32;
  cursor: not-allowed;
}

@media (max-width: 900px) {
  .status-block {
    min-height: 24px;
    gap: 0;
    line-height: 1.2;
  }

  .status-text,
  .skip-message {
    min-height: 0;
    font-size: 0.9rem;
  }

  .status-replay-btn {
    min-height: 38px;
  }
}

.status-text,
.skip-message {
  min-height: 20px;
}

.highlight {
  font-weight: 600;
  color: var(--accent-primary);
}

.status-emph {
  font-weight: 800;
  color: #b91c1c;
}
</style>